<template>
  <div class="state-dashboard">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <h2 class="generalTitle mb-0">Gestión de Estados</h2>
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary btn-sm"
              @click="refreshData"
              :disabled="loading"
            >
              <v-icon name="co-reload" scale="0.9" :class="{ 'spin': loading }" />
              Actualizar
            </button>
            <button
              class="btn btn-outline-primary btn-sm"
              @click="exportReport"
              :disabled="loading"
            >
              <v-icon name="bi-download" scale="0.9" />
              Exportar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <img src="@/assets/loaderPrimary.svg" height="100" alt="Cargando..." />
      <p class="mt-3 text-muted">Cargando datos de estados...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <v-icon name="hi-x-circle" scale="4" fill="#dc3545" />
      <h4 class="mt-3 text-danger">{{ error }}</h4>
      <button class="btn btn-primary mt-3" @click="refreshData">
        Reintentar
      </button>
    </div>

    <!-- Dashboard Content -->
    <div v-else>
      <!-- 1. Consulta de Transiciones Específicas -->
      <StateTransitionQuery
        v-if="filters.entityType === 'contract'"
        :contracts="rawData"
        :statusTranslations="statusTranslations"
        @error="handleQueryError"
      />

      <!-- 2. Distribución de Estados (Area Chart al 100%) -->
      <div class="row mt-4">
        <div class="col-12">
          <StateDistributionAreaChart
            :data="distributionData"
            :rawData="rawData"
            :title="'Distribución de Estados - ' + getEntityLabel(filters.entityType)"
            :statusTranslations="statusTranslations"
            @period-change="handleDistributionPeriodChange"
            @status-change="handleDistributionStatusChange"
          />
        </div>
      </div>

      <!-- 4. Tiempo Medio en Estado (Area Chart al 100%) -->
      <div class="row mt-4">
        <div class="col-12">
          <StateTimeAreaChart
            :data="timeData"
            :rawData="rawData"
            :title="'Tiempo Medio en Estado'"
            :statusTranslations="statusTranslations"
            @period-change="handleTimePeriodChange"
            @status-change="handleTimeStatusChange"
          />
        </div>
      </div>

      <!-- 5. Historial de Transiciones -->
      <div class="row mt-4">
        <div class="col-12">
          <StateTransitionTimeline
            :transitions="recentTransitions"
            :loading="loadingTransitions"
            :statusTranslations="statusTranslations"
            @loadMore="loadMoreTransitions"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import StateDistributionAreaChart from "./components/StateDistributionAreaChart.vue";
import StateTimeAreaChart from "./components/StateTimeAreaChart.vue";
import StateTransitionTimeline from "./components/StateTransitionTimeline.vue";
import StateTransitionQuery from "./components/StateTransitionQuery.vue";

export default {
  name: "StateManagementDashboard",
  components: {
    StateDistributionAreaChart,
    StateTimeAreaChart,
    StateTransitionTimeline,
    StateTransitionQuery,
  },
  data() {
    return {
      loading: true,
      loadingTransitions: false,
      error: null,
      rawData: [],

      // Filtros
      filters: {
        entityType: "contract",
        status: "",
        dateFrom: moment().subtract(7, "days").format("DD/MM/YYYY"),
        dateTo: moment().format("DD/MM/YYYY"),
        channel: "",
        tenant: "",
      },

      // Tipos de entidad
      entityTypes: [
        { value: "contract", label: "Contratos" },
        { value: "renewal", label: "Renovaciones" },
        { value: "cancellation", label: "Cancelaciones" },
        { value: "promotion", label: "Promociones" },
        { value: "issue", label: "Incidencias" },
      ],

      // Estados por entidad
      statusByEntity: {
        contract: [],
        renewal: [],
        cancellation: [],
        promotion: [],
        issue: [],
      },

      // Mapa de traducciones de estados
      statusTranslations: {},

      // Datos del dashboard
      distributionData: [],
      timeData: [],
      recentTransitions: [],
      blockingAlerts: [],

      pagination: {
        page: 1,
        limit: 100,
        total: 0,
      },
    };
  },
  computed: {
    isBusinessUser() {
      return this.$store.state.roles.Backoffice_member_empresas;
    },
  },
  created() {
    this.loadContractStatuses();
    this.loadDashboardData();
  },
  methods: {
    async loadContractStatuses() {
      try {
        const url = this.isBusinessUser
          ? "/business/contract/status"
          : "/contract/status-list";
        const response = await axios.get(url);

        this.statusByEntity.contract = response.data.map((s) => s.name);

        response.data.forEach((s) => {
          if (s.name && s.statusDescription) {
            this.statusTranslations[s.name] = s.statusDescription;
          }
        });

        this.addAdditionalTranslations();
      } catch (error) {
        console.error("Error loading contract statuses:", error);
        this.addAdditionalTranslations();
      }
    },

    addAdditionalTranslations() {
      const additionalTranslations = {
        "PENDING_CUSTOMER_APPROVAL": "Pendiente Aprobación Cliente",
        "APPROVED_BY_CUSTOMER": "Aprobado por Cliente",
        "REJECTED_BY_CUSTOMER": "Rechazado por Cliente",
        "APPROVED_BY_BO": "Aprobado por BO",
        "REJECTED_BY_BO": "Rechazado por BO",
        "PENDING_LOGISTICS_PROCESSING": "Logística Pendiente",
        "RENEWAL_FEE_PAYMENT_COMPLETED": "Pago Renovación Completado",
        "RENEWAL_FEE_PAYMENT_FAILED": "Pago Renovación Fallido",
        "CONTRACT_RENEWAL_INITIAL_PAYMENT_ON_HOLD": "Pago Inicial en Hold",
        "PARENT_CONTRACT_CANCELLED": "Contrato Padre Cancelado",
        "PROCESS_STARTED": "Proceso Iniciado",
        "PAYMENT_HOLD_SUBMITTED": "Hold de Pago Enviado",
        "LOGISTIC_PICKUP_REQUESTED": "Recogida Solicitada",
        "REVIEWED_OK": "Revisado OK",
        "REVIEWED_DEFECTS": "Revisado con Defectos",
        "EXECUTED": "Ejecutado",
        "EXPIRED": "Expirado",
        "ACTIVATED": "Activada",
        "DEACTIVATED": "Desactivada",
        "DELETED": "Eliminada",
        "ASSIGNED": "Asignada",
        "IN_PROGRESS": "En Progreso",
        "IN_ANALYSIS": "En Análisis",
        "DONE": "Resuelta",
        "CREATED": "Creado",
        "PENDING": "Pendiente",
        "COMPLETED": "Completado",
        "CANCELLED": "Cancelado",
        "REJECTED": "Rechazado",
        "DELIVERED": "Entregado",
        "ACTIVE": "Activo",
      };

      Object.entries(additionalTranslations).forEach(([key, value]) => {
        if (!this.statusTranslations[key]) {
          this.statusTranslations[key] = value;
        }
      });

      this.statusByEntity.renewal = [
        "CREATED", "PENDING_CUSTOMER_APPROVAL", "APPROVED_BY_CUSTOMER",
        "REJECTED_BY_CUSTOMER", "APPROVED_BY_BO", "REJECTED_BY_BO",
        "PENDING_LOGISTICS_PROCESSING", "RENEWAL_FEE_PAYMENT_COMPLETED",
        "RENEWAL_FEE_PAYMENT_FAILED", "CONTRACT_RENEWAL_INITIAL_PAYMENT_ON_HOLD",
        "PARENT_CONTRACT_CANCELLED", "COMPLETED", "CANCELLED"
      ];
      this.statusByEntity.cancellation = [
        "CREATED", "PROCESS_STARTED", "PAYMENT_HOLD_SUBMITTED",
        "LOGISTIC_PICKUP_REQUESTED", "DELIVERED", "REVIEWED_OK",
        "REVIEWED_DEFECTS", "EXECUTED", "COMPLETED", "CANCELLED", "EXPIRED"
      ];
      this.statusByEntity.promotion = ["PENDING", "ACTIVATED", "DEACTIVATED", "DELETED"];
      this.statusByEntity.issue = ["CREATED", "ASSIGNED", "IN_PROGRESS", "IN_ANALYSIS", "DONE", "REJECTED"];
    },

    async loadDashboardData() {
      this.loading = true;
      this.error = null;

      try {
        await this.fetchEntityData();
        this.processDistribution();
        this.processTimeMetrics();
        this.processTransitions();
        this.processAlerts();
      } catch (error) {
        this.error = "Error al cargar los datos. Por favor, inténtelo de nuevo.";
        console.error("Dashboard error:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchEntityData() {
      const dateFrom = this.$dateFrontToBack(this.filters.dateFrom);
      const dateTo = this.$dateFrontToBack(this.filters.dateTo);

      let response;
      const entityType = this.filters.entityType;

      switch (entityType) {
        case "contract":
          response = await this.fetchContracts(dateFrom, dateTo);
          break;
        case "renewal":
          response = await this.fetchRenewals(dateFrom, dateTo);
          break;
        case "cancellation":
          response = await this.fetchCancellations(dateFrom, dateTo);
          break;
        case "promotion":
          response = await this.fetchPromotions();
          break;
        case "issue":
          response = await this.fetchIssues(dateFrom, dateTo);
          break;
        default:
          response = { items: [], total: 0 };
      }

      this.rawData = response.items;
      this.pagination.total = response.total;
    },

    async fetchContracts(dateFrom, dateTo) {
      const url = this.isBusinessUser ? "/business/contract/search" : "/contract/search";

      const params = {
        "pagination.pageSize": this.pagination.limit,
        "pagination.page": this.pagination.page,
      };

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (this.filters.status) params.statuses = this.filters.status;
      if (this.filters.channel) params.channel = this.filters.channel;

      const response = await axios.get(url, { params });

      return {
        items: response.data.contracts || [],
        total: response.data.pagination?.totalElements || 0,
      };
    },

    async fetchRenewals(dateFrom, dateTo) {
      const params = {
        pageSize: this.pagination.limit,
        page: this.pagination.page,
      };

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (this.filters.status) params.status = this.filters.status;

      const response = await axios.get("/contract-renewal/search", { params });

      const renewals = (response.data.renewals || []).map(r => ({
        ...r,
        status: r.status?.status || r.status || "UNKNOWN",
        date: r.dateOfRequest,
        contractId: r.newContractId,
      }));

      return {
        items: renewals,
        total: response.data.totalElements || 0,
      };
    },

    async fetchCancellations(dateFrom, dateTo) {
      const params = {
        pageSize: this.pagination.limit,
        page: this.pagination.page,
      };

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (this.filters.status) params.statuses = this.filters.status;

      const response = await axios.get("/contract/request/cancellation/search", { params });

      return {
        items: response.data.responseCancellations || [],
        total: response.data.totalElements || 0,
      };
    },

    async fetchPromotions() {
      const params = {
        "pagination.pageSize": this.pagination.limit,
        "pagination.page": this.pagination.page,
      };

      if (this.filters.status) params.status = this.filters.status;

      const response = await axios.get("/promotions/promotion/search", { params });

      return {
        items: response.data.promotions || [],
        total: response.data.pagination?.totalElements || 0,
      };
    },

    async fetchIssues(dateFrom, dateTo) {
      const params = {
        "pagination.pageSize": this.pagination.limit,
        "pagination.page": this.pagination.page,
      };

      if (dateFrom) params.createdAtFrom = dateFrom;
      if (dateTo) params.createdAtTo = dateTo;
      if (this.filters.status) params.status = this.filters.status;

      const response = await axios.get("/incidents", { params });

      return {
        items: response.data.incidents || [],
        total: response.data.totalElements || 0,
      };
    },

    processDistribution() {
      const items = this.rawData;
      const statusCount = {};

      items.forEach((item) => {
        const status = this.getItemStatus(item);
        statusCount[status] = (statusCount[status] || 0) + 1;
      });

      this.distributionData = Object.entries(statusCount)
        .map(([status, count]) => ({
          status,
          count,
          percentage: items.length > 0 ? ((count / items.length) * 100).toFixed(1) : 0,
        }))
        .sort((a, b) => b.count - a.count);
    },

    processTimeMetrics() {
      const items = this.rawData;
      const stateTimeData = {};

      items.forEach((item) => {
        const status = this.getItemStatus(item);
        const createdAt = moment(item.createdAt || item.date || item.dateOfRequest);
        const updatedAt = moment(item.updatedAt || new Date());

        if (createdAt.isValid() && updatedAt.isValid()) {
          const hoursInState = updatedAt.diff(createdAt, "hours");

          if (!stateTimeData[status]) {
            stateTimeData[status] = { total: 0, count: 0 };
          }
          stateTimeData[status].total += hoursInState;
          stateTimeData[status].count += 1;
        }
      });

      this.timeData = Object.entries(stateTimeData)
        .map(([status, data]) => ({
          status,
          avgHours: data.count > 0 ? Math.round(data.total / data.count) : 0,
          avgDays: data.count > 0 ? (data.total / data.count / 24).toFixed(1) : "0",
        }))
        .sort((a, b) => b.avgHours - a.avgHours);
    },

    processTransitions() {
      const items = this.rawData.slice(0, 20);

      this.recentTransitions = items.map((item, index) => {
        const status = this.getItemStatus(item);
        return {
          id: item.id || item.contractId || index,
          entityType: this.filters.entityType,
          entityId: item.contractId || item.newContractId || item.id,
          fromStatus: this.getPreviousStatus(status),
          toStatus: status,
          timestamp: item.updatedAt || item.date || item.dateOfRequest || new Date().toISOString(),
          user: item.updatedBy || item.assignedTo || "Sistema",
        };
      });
    },

    processAlerts() {
      const items = this.rawData;
      const blockingStatesList = this.getBlockingStates();

      this.blockingAlerts = blockingStatesList
        .map((status) => {
          const count = items.filter((item) => {
            const itemStatus = this.getItemStatus(item);
            return itemStatus.toUpperCase() === status.toUpperCase();
          }).length;

          if (count > 0) {
            return {
              status,
              count,
              severity: count > 10 ? "high" : count > 5 ? "medium" : "low",
              message: `${count} ${this.getEntityLabel(this.filters.entityType).toLowerCase()} en estado "${this.formatStatus(status)}"`,
            };
          }
          return null;
        })
        .filter(Boolean);
    },

    // Helpers
    getItemStatus(item) {
      if (item.status) {
        if (typeof item.status === "object") {
          return item.status.status || "UNKNOWN";
        }
        return item.status;
      }
      return item.state || "UNKNOWN";
    },

    getBlockingStates() {
      const blockingStates = {
        contract: ["PENDING_VALIDATION", "PENDING_PAYMENT", "REJECTED", "PENDING_SCORING"],
        renewal: ["PENDING_CUSTOMER_APPROVAL", "RENEWAL_FEE_PAYMENT_FAILED", "REJECTED_BY_CUSTOMER"],
        cancellation: ["LOGISTIC_PICKUP_REQUESTED", "REVIEWED_DEFECTS"],
        promotion: ["PENDING"],
        issue: ["CREATED", "IN_ANALYSIS"],
      };
      return blockingStates[this.filters.entityType] || [];
    },

    getPreviousStatus(currentStatus) {
      const transitions = {
        "ACTIVE": "PENDING_PAYMENT",
        "PENDING_PAYMENT": "PENDING_VALIDATION",
        "COMPLETED": "ACTIVE",
        "CANCELLED": "ACTIVE",
        "APPROVED_BY_CUSTOMER": "PENDING_CUSTOMER_APPROVAL",
        "APPROVED_BY_BO": "APPROVED_BY_CUSTOMER",
      };
      return transitions[currentStatus] || "CREATED";
    },

    formatStatus(status) {
      if (!status) return "Desconocido";
      const upperStatus = status.toUpperCase();
      if (this.statusTranslations[upperStatus]) {
        return this.statusTranslations[upperStatus];
      }
      if (this.statusTranslations[status]) {
        return this.statusTranslations[status];
      }
      return status.replace(/_/g, " ");
    },

    getEntityLabel(type) {
      const entity = this.entityTypes.find((e) => e.value === type);
      return entity ? entity.label : type;
    },

    refreshData() {
      this.loadDashboardData();
    },

    exportReport() {
      const headers = ["ID", "Estado", "Fecha"];
      const rows = this.rawData.map((item) => {
        const status = this.getItemStatus(item);
        return [
          item.contractId || item.id,
          this.formatStatus(status),
          item.createdAt || item.date || ""
        ].map(v => `"${v || ""}"`).join(",");
      });
      const csv = [headers.join(","), ...rows].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `gestion_estados_${moment().format("YYYY-MM-DD")}.csv`;
      link.click();
    },

    showAlertDetails(alert) {
      this.filters.status = alert.status;
      this.loadDashboardData();
    },

    loadMoreTransitions() {
      // Cargar más transiciones
    },

    handleDistributionPeriodChange(/* period */) {
      // Manejar cambio de período en distribución
    },

    handleDistributionStatusChange(/* status */) {
      // Manejar cambio de estado en distribución
    },

    handleTimePeriodChange(/* period */) {
      // Manejar cambio de período en tiempo
    },

    handleTimeStatusChange(/* status */) {
      // Manejar cambio de estado en tiempo
    },

    handleQueryError(errorMessage) {
      console.error("Error en consulta de transiciones:", errorMessage);
      this.error = errorMessage;
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables.scss';

.state-dashboard {
  padding: 0;
}

.gap-2 {
  gap: 0.5rem;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;

  .ov-icon {
    margin-right: 0.25rem;
  }
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
